import { create } from 'zustand'
import { toastManager } from '../ui/Toast/toastManager'

type ToastType = 'error' | 'success' | 'info'

/** Optional inline action (e.g. an Undo button). When present the toast lingers
 *  longer so the user has time to click it. */
interface ToastAction {
  label: string
  onClick: () => void
}

interface Toast {
  id: string
  message: string
  type: ToastType
  action?: ToastAction
}

interface ToastStore {
  toasts: Toast[]
  addToast: (message: string, type?: ToastType, action?: ToastAction) => void
  removeToast: (id: string) => void
}

let counter = 0

const PLAIN_MS = 4_000
const ACTION_MS = 9_000
const MAX_VISIBLE = 3

const TONE = { error: 'danger', success: 'success', info: 'info' } as const

// Container side adapted to Base UI's toast manager (META rule 2); public API
// unchanged. Danger toasts do not auto-dismiss (Toast card).
export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],

  addToast: (message, type = 'error', action) => {
    const id = `toast-${++counter}`
    set((s) => ({ toasts: [...s.toasts, { id, message, type, action }] }))
    toastManager.add({
      id,
      title: message,
      type: TONE[type],
      priority: type === 'error' ? 'high' : 'low',
      // Action toasts (undo) get a longer window — matches the time a
      // reversible operation stays easy to call back.
      timeout: type === 'error' ? 0 : action ? ACTION_MS : PLAIN_MS,
      actionProps: action && {
        children: action.label,
        onClick: () => {
          action.onClick()
          get().removeToast(id)
        },
      },
      onRemove: () =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    })
    if (get().toasts.length > MAX_VISIBLE) {
      get().removeToast(get().toasts[0].id)
    }
  },

  removeToast: (id) => {
    toastManager.close(id)
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  },
}))
